/**
 * src/pages/BillingSettings.jsx
 * Gestión de suscripción: estado, portal Stripe, cancelación.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { getSuscripcion, abrirPortal, verificarPlanesFlow, diagnosticoFlow, reconciliarFlow } from '../api/billing'

const ESTADO_COLOR = {
  active:    'green',
  trialing:  'blue',
  past_due:  'yellow',
  canceled:  'gray',
}

export default function BillingSettings() {
  const [portalLoading, setPortalLoading] = useState(false)
  const { data: suscripcion, isLoading } = useQuery({
    queryKey: ['billing', 'suscripcion'],
    queryFn: getSuscripcion,
  })

  async function handlePortal() {
    setPortalLoading(true)
    try {
      const { url } = await abrirPortal()
      window.location.href = url
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al abrir el portal')
    } finally { setPortalLoading(false) }
  }

  // Sonda read-only de planes en Flow (Paso 1 de validación de Flow)
  const [sonda, setSonda] = useState(null)        // { entorno, planes, resumen } | { error }
  const [sondaLoading, setSondaLoading] = useState(false)

  async function handleSonda() {
    setSondaLoading(true); setSonda(null)
    try {
      setSonda(await verificarPlanesFlow())
    } catch (e) {
      setSonda({ error: e.response?.data?.detail || 'No se pudo consultar Flow.' })
    } finally { setSondaLoading(false) }
  }

  // Diagnóstico: trae de Flow la suscripción ya existente (sin pagar de nuevo)
  const [diag, setDiag] = useState(null)
  const [diagLoading, setDiagLoading] = useState(false)

  async function handleDiag() {
    setDiagLoading(true); setDiag(null)
    try {
      setDiag(await diagnosticoFlow())
    } catch (e) {
      setDiag({ error: e.response?.data?.detail || 'No se pudo consultar Flow.' })
    } finally { setDiagLoading(false) }
  }

  // Reconciliar: activa en la BD las suscripciones ya pagadas en Flow (webhook fallido)
  const [recon, setRecon] = useState(null)
  const [reconLoading, setReconLoading] = useState(false)
  const [forzarSubId, setForzarSubId] = useState('')

  async function handleRecon(forzar = false) {
    if (forzar && !forzarSubId.trim()) {
      setRecon({ error: 'Para forzar, indica el subscription_id (sus_...).' }); return
    }
    if (forzar && !window.confirm(
      `Vas a ACTIVAR ${forzarSubId.trim()} aunque Flow no confirme el pago. ` +
      'Úsalo solo con un cargo verificado en el banco. ¿Continuar?')) return
    setReconLoading(true); setRecon(null)
    try {
      setRecon(await reconciliarFlow(forzar ? forzarSubId.trim() : undefined, forzar))
    } catch (e) {
      setRecon({ error: e.response?.data?.detail || 'No se pudo reconciliar con Flow.' })
    } finally { setReconLoading(false) }
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Facturación" subtitle="Gestión de suscripción y pagos" />
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', maxWidth: '600px' }}>
        <Card title="Suscripción actual">
          {isLoading ? (
            <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>Cargando...</div>
          ) : !suscripcion?.tier ? (
            <div>
              <p style={{ color: 'var(--color-text-2)', fontSize: '14px', marginBottom: '16px' }}>No tienes una suscripción activa.</p>
              <Button onClick={() => window.location.href = '/pricing'}>Ver planes →</Button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                ['Plan', <span style={{ fontFamily: 'var(--font-mono)', textTransform: 'capitalize', fontSize: '13px' }}>{suscripcion.tier}</span>],
                ['Estado', <Badge color={ESTADO_COLOR[suscripcion.estado] || 'gray'} dot>{suscripcion.estado}</Badge>],
                ['Período hasta', suscripcion.periodo_fin ? new Date(suscripcion.periodo_fin).toLocaleDateString('es-CL') : '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--color-text-2)' }}>{label}</span>
                  <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>{value}</span>
                </div>
              ))}
              <div style={{ marginTop: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Button onClick={handlePortal} loading={portalLoading} variant="secondary"
                  icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>}
                >
                  Gestionar suscripción
                </Button>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
                Los cambios de plan, cancelación y facturas se gestionan desde el portal de Stripe.
              </p>
            </div>
          )}
        </Card>

        {/* Sonda de planes en Flow (validación) */}
        <Card title="Planes en Flow (sonda)" subtitle="Comprueba que los 6 planes están configurados. No mueve dinero.">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: sonda ? '14px' : 0 }}>
            <Button onClick={handleSonda} loading={sondaLoading}>Verificar planes</Button>
            {sonda && !sonda.error && (
              <>
                <Badge color={sonda.entorno === 'sandbox' ? 'blue' : 'yellow'}>
                  Entorno: {sonda.entorno === 'sandbox' ? 'sandbox (pago de prueba)' : 'REAL (cobra dinero)'}
                </Badge>
                <span style={{ fontSize: '12px', color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>{sonda.resumen}</span>
              </>
            )}
          </div>

          {sonda?.error && (
            <div style={{ fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {sonda.error}</div>
          )}

          {sonda?.planes && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {sonda.planes.map((p) => {
                const ok = p.estado === 'ok' && p.activo_en_flow
                const color = ok ? 'var(--color-ok)' : 'var(--color-error)'
                return (
                  <div key={p.env_var} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border)', gap: '10px' }}>
                    <span style={{ fontSize: '13px', textTransform: 'capitalize' }}>{p.tier} · {p.ciclo}</span>
                    <span style={{ fontSize: '12px', color, fontFamily: 'var(--font-mono)', textAlign: 'right' }}>
                      {ok
                        ? `✓ ${p.monto ? p.monto.toLocaleString('es-CL') + ' ' + (p.moneda || '') : 'activo'}`
                        : `✗ ${p.estado}${p.detalle ? ' — ' + p.detalle : ''}`}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </Card>

        {/* Diagnóstico Flow: inspeccionar la suscripción ya existente (sin pagar) */}
        <Card title="Diagnóstico Flow (mi suscripción)" subtitle="Trae de Flow la última suscripción existente en la BD. Read-only, no cobra.">
          <Button onClick={handleDiag} loading={diagLoading}>Consultar suscripción en Flow</Button>
          {diag && (
            <pre style={{
              marginTop: '14px', padding: '12px', background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border)', borderRadius: '8px',
              fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto', maxHeight: '460px',
            }}>
              {JSON.stringify(diag, null, 2)}
            </pre>
          )}
        </Card>

        {/* Reconciliar: activa suscripciones ya pagadas cuyo webhook no llegó */}
        <Card title="Reconciliar con Flow" subtitle="Consulta Flow y activa las suscripciones ya pagadas cuyo webhook no llegó. Idempotente.">
          <Button onClick={() => handleRecon(false)} loading={reconLoading}>Reconciliar suscripciones</Button>

          {/* Forzado excepcional: pago verificado en banco pero Flow no lo confirma */}
          <details style={{ marginTop: '16px' }}>
            <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
              Forzar activación (pago verificado fuera de Flow)
            </summary>
            <div style={{ marginTop: '10px', padding: '12px', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
              <p style={{ fontSize: '12px', color: 'var(--color-text-3)', marginBottom: '10px' }}>
                Activa la suscripción aunque Flow la reporte impaga. Úsalo solo si el cargo
                está verificado en el banco (discrepancia Flow↔Webpay). Queda registrado.
              </p>
              <input
                value={forzarSubId}
                onChange={(e) => setForzarSubId(e.target.value)}
                placeholder="sus_xxxxxxxxxx"
                style={{ width: '100%', padding: '8px 10px', marginBottom: '10px', fontSize: '13px',
                         fontFamily: 'var(--font-mono)', border: '1px solid var(--color-border)',
                         borderRadius: '6px', background: 'var(--color-surface-2)', color: 'var(--color-text)' }}
              />
              <Button onClick={() => handleRecon(true)} loading={reconLoading} variant="secondary">
                Forzar activación
              </Button>
            </div>
          </details>
          {recon && !recon.error && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ fontSize: '13px', color: 'var(--color-text)', marginBottom: '10px' }}>
                Revisadas: <b>{recon.revisadas}</b> · Activadas: <b style={{ color: 'var(--color-ok)' }}>{recon.activadas}</b>
              </div>
              {(recon.detalle || []).map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '7px 0', borderBottom: '1px solid var(--color-border)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}>
                  <span style={{ color: 'var(--color-text-2)', wordBreak: 'break-all' }}>{d.subscription_id}</span>
                  <span style={{ color: d.resultado === 'activada' ? 'var(--color-ok)' : 'var(--color-text-3)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {d.resultado === 'activada' ? `✓ activada${d.periodo_fin ? ' · hasta ' + d.periodo_fin.slice(0, 10) : ''}` : d.resultado}
                  </span>
                </div>
              ))}
            </div>
          )}
          {recon?.error && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {recon.error}</div>
          )}
        </Card>
      </div>
    </div>
  )
}
